import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import axios from 'axios';

const CURRENCY_SYMBOLS = {
  DKK: 'kr',
  USD: '$',
  EUR: '€',
  GBP: '£',
  SEK: 'kr',
  NOK: 'kr',
  JPY: '¥',
  AUD: '$',
  CAD: '$',
  CHF: 'Fr',
  CNY: '¥'
};

const CURRENCY_DECIMALS = {
  DKK: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  SEK: 2,
  NOK: 2,
  JPY: 0,  // No decimals
  AUD: 2,
  CAD: 2,
  CHF: 2,
  CNY: 2
};

const CURRENCY_NAMES = {
  DKK: 'Danish Krone',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',
  JPY: 'Japanese Yen',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan'
};

const CurrencyContext = createContext({
  currency: 'DKK', // Display in store's native currency
  storeCurrency: 'DKK', // Store's native currency
  setCurrency: () => {},
  convertAmount: () => 0,
  formatAmount: () => '',
  getCurrencySymbol: () => '',
  getDecimalPlaces: () => 2,
  loading: true,
  availableCurrencies: [],
  saveCurrencyPreference: async () => {}
});

export function CurrencyProvider({ children }) {
  const { session } = useAuth();
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('display_currency') || null); // Initialize from localStorage
  const [storeCurrency, setStoreCurrency] = useState('DKK'); // Store's native currency (from WooCommerce)
  const [loading, setLoading] = useState(true);

  // Load store's currency from backend (auto-detects from WooCommerce)
  useEffect(() => {
    if (session?.access_token) {
      loadStoreCurrency();
    } else {
      // No session, use local storage or default
      const saved = localStorage.getItem('display_currency');
      setCurrencyState(saved || 'DKK');
      setLoading(false);
    }
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStoreCurrency = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

      // Parallel fetch: Store info & User profile
      const [storeResp, profileResp] = await Promise.all([
        axios.get(`${API_URL}/auth/store`, { headers: { 'Authorization': `Bearer ${session.access_token}` } }),
        supabase.from('user_profiles').select('display_currency').eq('user_id', session.user.id).maybeSingle()
      ]);

      const store = storeResp?.data?.store || {};
      const detectedCurrency = store.store_currency || 'DKK';
      setStoreCurrency(detectedCurrency);

      // Use user's preferred currency if set, otherwise default to local storage or store currency
      const userPref = profileResp.data?.display_currency;
      const localPref = localStorage.getItem('display_currency');
      const finalCurrency = userPref || localPref || detectedCurrency;
      
      setCurrencyState(finalCurrency);
      
      // Sync local storage if we have a valid preference
      if (finalCurrency) {
        localStorage.setItem('display_currency', finalCurrency);
      }

      console.log('✅ Currency resolved:', { store: detectedCurrency, display: finalCurrency });
    } catch (error) {
      console.error('❌ Error loading currency settings:', error);
      // Fallback to local storage or DKK
      setCurrencyState(localStorage.getItem('display_currency') || 'DKK');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrencyPreference = useCallback(async (newCurrency) => {
    try {
      setCurrencyState(newCurrency);
      localStorage.setItem('display_currency', newCurrency);
      
      const { error } = await supabase
        .from('user_profiles')
        .upsert({ 
          user_id: session.user.id,
          display_currency: newCurrency,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;
      return { success: true };
    } catch (err) {
      console.error('Failed to save currency preference:', err);
      return { success: false, error: err };
    }
  }, [session]);

  // No conversion; UI uses store currency for display
  const convertAmount = useCallback((amount) => amount ?? 0, []);

  // Format amount with proper symbol and positioning for store currency
  const formatAmount = useCallback((amount, _fromCurrency = null, options = {}) => {
    const value = typeof amount === 'number' ? amount : 0;
    const decimals = options.decimals ?? CURRENCY_DECIMALS[currency];
    const symbol = CURRENCY_SYMBOLS[currency];
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    const symbolAfter = ['DKK', 'SEK', 'NOK'].includes(currency);
    return symbolAfter ? `${formatted} ${symbol}` : `${symbol}${formatted}`;
  }, [currency]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_SYMBOLS[currency];
  }, [currency]);

  // Get decimal places for currency
  const getDecimalPlaces = useCallback(() => {
    return CURRENCY_DECIMALS[currency];
  }, [currency]);

  // List of available currencies
  const availableCurrencies = Object.keys(CURRENCY_SYMBOLS).map(code => ({
    code,
    name: CURRENCY_NAMES[code],
    symbol: CURRENCY_SYMBOLS[code],
    decimals: CURRENCY_DECIMALS[code]
  }));

  const value = {
    currency,
    storeCurrency, // 180 IQ: Expose store's native currency
    setCurrency: setCurrencyState,
    convertAmount,
    formatAmount,
    getCurrencySymbol,
    getDecimalPlaces,
    loading,
    availableCurrencies,
    saveCurrencyPreference
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

// Custom hook to use currency context
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}

